import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { AppState } from '../state/app.state';
import { sendInvite } from '@shared/services/TeamService';
import { failureNotification } from "@shared/store/notification.duck";
import { enrichError } from '@shared/utils/error-helper';
import { TeamEntity } from '@shared/models/team.models';
import { loadAccountSummary } from "@features/settings/store/payments.duck";
import { loadTeamMovementById } from "@features/whereabouts/store/companyMovements.duck";
import { BasicUserInfo } from '@shared/models/user.models';
import { updateUsersTeam } from '@features/settings/services/UserService';
import { setCalendarDate } from "@shared/store/dashboard.duck";
import moment from "moment";
import { loadConfig } from "@shared/store/config.duck";

enum AddMemberDialogMode {
  Add, Move,
}

export interface AddMemberDialogState {
  isOpen: boolean;
  processing: boolean;
  success: boolean;
  error: string;
  mode: AddMemberDialogMode;
  teamToAddTo: TeamEntity | undefined;
  failedRequest: {
    firstname: string;
    lastname: string;
    email: string;
  }
}

export const initialAddMemberState: AddMemberDialogState = {
  isOpen: false,
  processing: false,
  success: false,
  error: '',
  mode: AddMemberDialogMode.Move,
  teamToAddTo: undefined,
  failedRequest: {
    firstname: '',
    lastname: '',
    email: '',
  }
}


const moveTeamMember: any = createAsyncThunk(
  'addMemberDialog/moveTeamMember',
  async (params: {user: BasicUserInfo, team: TeamEntity}, thunkAPI) => {
    await updateUsersTeam(params.user.userId, params.team.id);
    thunkAPI.dispatch(loadConfig(['teams']));
  }
)

export const addNewTeamMember = createAsyncThunk(
  'addMemberDialog/addMember',
  async (addMemberDetails: any, thunkAPI) => {
    const { firstname, lastname, email, teamId } = addMemberDetails;
    await thunkAPI.dispatch(setProcessing());
    try {
      await sendInvite(email, firstname, lastname, teamId);
      await thunkAPI.dispatch(setSuccess());

      await thunkAPI.dispatch(loadTeamMovementById(teamId));
      await thunkAPI.dispatch(loadAccountSummary());

      // Hack to refresh team
      await thunkAPI.dispatch(setCalendarDate(moment((thunkAPI.getState() as AppState).dashboard.calendarDate)));

    } catch (err: any) {
      const errResp = enrichError(JSON.parse(err.message).message);
      await thunkAPI.dispatch(setError({ message: errResp.description, details: addMemberDetails }));
      await thunkAPI.dispatch(failureNotification(errResp.description));
    }
  }
);

const addMemberDialogSlice = createSlice({
  name: 'editMovements',
  initialState: initialAddMemberState,
  reducers: {
    setTeamToAddUserTo: (state, action) => ({...state, teamToAddTo: action.payload}),
    closeAddMemberDialog: () => initialAddMemberState,
    setProcessing: (state) => ({...state, processing: true, success: false, error: ''}),
    setSuccess: (state) => ({...state, processing: false, success: true, error: ''}),
    setAddMemberDialogMode: (state, action) => ({
      ...state,
      isOpen: state.isOpen,
      mode: action.payload,

    }),
    setError: (state, action) => ({
      ...state,
      processing: false,
      success: false,
      error: action.payload.message,
      failedRequest: action.payload.details
    }),
    resetAddMember: (state) => ({
      ...initialAddMemberState,
      mode: AddMemberDialogMode.Add,
      teamToAddTo: state.teamToAddTo,
      success: false,
      processing: false,
      isOpen: state.isOpen,
    }),
    resetAddExistingMember: (state) => ({
      ...initialAddMemberState,
      mode: AddMemberDialogMode.Move,
      teamToAddTo: state.teamToAddTo,
      success: false,
      processing: false,
      isOpen: state.isOpen,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(moveTeamMember.pending, (state) => ({...state, processing: true, success: false}))
      .addCase(moveTeamMember.rejected, (state) => ({...state, processing: false, success: false}))
      .addCase(moveTeamMember.fulfilled, (state) => ({...state, processing: false, success: true}));
  }
})

export default addMemberDialogSlice.reducer;
export const { setError, resetAddMember } = addMemberDialogSlice.actions;
const { setProcessing, setSuccess } = addMemberDialogSlice.actions;

// Selectors
export const selectIsAddMemberDialogOpen = (state: AppState) => state.addMemberDialog.isOpen;
const selectActiveAddMemberDialogMode = (state: AppState) => state.addMemberDialog.mode;
const selectTeamToAddTo = (state: AppState) => {
  return state.addMemberDialog.teamToAddTo
};
export const selectAddMemberProcessingState = (state: AppState) => ({
  processing: state.addMemberDialog.processing,
  error: state.addMemberDialog.error,
  success: state.addMemberDialog.success,
  failedRequest: state.addMemberDialog.failedRequest,
});
